<script setup>
import SimpleTable from "@/components/table/SimpleTable.vue";
import ModalWebhook from "@/views/developer/components/ModalWebhook.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonSearcherSimplified from "@/components/searcher/ArgonSearcherSimplified.vue";
import { ref, onMounted, watch, inject, computed } from "vue";
import { webhookStore } from "@/_store/webhookStore";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import moment from "moment";
import ArgonBadge from "@/components/ArgonBadge";
import ArgonSwitch from "@/components/ArgonSwitch.vue";
import { errorUtils } from "@/utils";


const sweetAlert = inject("$swal");
const snackbar = ref(null);
const whStore = webhookStore();
const showModal = ref(false);
const searchQuery = ref(""); 
const whId= ref(null);
const headers = [
  { header: "Activado", field: "active", sort: false },
  { header: "Nombre", field: "name", sort: false, width: "w-28" },
  { header: "URL", field: "uri", sort: false , width: "w-30"  },
  { header: "Tema", field: "event_type", sort: false, position: "center"},
  { header: "Creado", field: "created_at", sort: false },
  { header: "", sort: false },
];

const listTable = ref({ data: [] });

function getWebhook() {
  whStore.getWebhooks(); 
}

function newWebhook() {
  whId.value= null
  showModal.value = true;
}
function edit(id){
  whId.value= id
  showModal.value = true;
}
function onSwitchChange(id){
  let contract = listTable.value.data.find(item => item._id === id);
  if (!contract) return;
  whStore.editWebhook(contract)
}
function onWebhookSuccess() {
  getWebhook();
}
function deleteWebhook(item) {
  sweetAlert({
    title: "¿Está seguro que desea eliminar?",
    text: "¡No podrá revertir esta acción!",
    showCancelButton: true,
    confirmButtonText: "Eliminar",
    cancelButtonText: "Cancelar",
    reverseButtons: true,
    customClass: {
      confirmButton: `btn btn-xs  ms-1 bg-gradient-danger`,
      cancelButton: "btn btn-xs btn-outline-primary me-1",
    },
    buttonsStyling: false,
  }).then((result) => {
    if (result.isConfirmed) whStore.deleteWebhook(item);
  });
}

function getPosition(field) {
  const column = headers.find((col) => col.field === field);
  return column && column.position ? column.position : "start";
}

function handleSearch(query) {
  searchQuery.value = query;
}
function getType(type){
  if(type === 'STOP'){
    return 'Parada';
  }
  return 'Milla Intermedia';
}

const  filteredData = computed(() =>{
  if (!searchQuery.value) {
    return listTable.value.data;
  }
  
  const searchLower = searchQuery.value.toLowerCase();
  return  listTable.value.data.filter(item => 
    item.name.toLowerCase().includes(searchLower)
  );
});

watch(() => whStore.error, (value) => {
  if (value) {
    snackbar.value = {
      title: "Error",
      message: errorUtils.getErrorMessageHtml(value),
      type: "danger",
      color: "white",
    };
    getWebhook();
  }
});

watch(() => whStore.deleted, (value) => {
  if (value) {
    snackbar.value = {
      title: "Eliminado",
      message: "Se ha eliminado la configuración",
      type: "info",
      color: "white",
    };
    getWebhook();
  }
});

watch(() => whStore.list, (value) => {
  if (value) {
    listTable.value.data = value;
  }
});

const closeSnackbar = () => {
  snackbar.value = null;
};

onMounted(() => {
  getWebhook();
});
</script>

<template>
  <div class="card h-100">
    <div class="card-header">
      Webhooks
    </div>
    <div class="card-body">
      <div class="d-flex align-items-center mb-2">
        <argon-button             
          class="btn-xs"
          color="primary"
          variant="outline" 
          @click="newWebhook">
          <i class="fa fa-plus"></i>
          Nuevo
        </argon-button>
        <argon-searcher-simplified class="ms-auto" @search="handleSearch" />
      </div>
      <simple-table
        :headers="headers"
        :items="{ data: filteredData }"
      >
        <template #default="{ item }">
          <argon-td :position="getPosition('active')">
            <argon-switch
              id="active"
              v-model="item.active"
              name="active"
              @change="onSwitchChange(item._id)"
            />
          </argon-td>
          <argon-td
            :text-secondary="item.name"
            :position="getPosition('name')"
          />
          <argon-td :position="getPosition('uri')">
            <span
              v-tooltip="item.uri"
              class="uri-text text-secondary"
            >
              {{ item.uri }}
            </span>
          </argon-td>
          <argon-td
            :position="getPosition('event_type')"
          >
              <argon-badge :color="'secondary'" style="min-width: 120px;" >
              {{getType(item.event_type)}}
            </argon-badge>
          </argon-td>

          <argon-td
            :position="getPosition('created_at')"
            :text-secondary="moment(item.created_at).format('DD/MM/YYYY')"
          />
          <argon-td>
            <div class="">
              <a id="btEdit" href="javascript:;" @click="edit(item._id)">
                <i
                  v-tooltip="'Modificar'"
                  class="fa fa-pencil text-sm ms-2 hover"
                />
              </a>
              <a id="btDelete"  href="javascript:;" @click="deleteWebhook(item._id)">
                  <i
                    v-tooltip="'Eliminar'"
                    class="fa fa-trash text-danger text-xs ms-2 hover"
                  ></i>
              </a>
            </div>
          </argon-td>
        </template>
      </simple-table>
    </div>
  </div>

  <modal-webhook
    v-if="showModal"
    :id="whId"
    @success="onWebhookSuccess"
    @close="showModal = false"
  />

  <div class="position-fixed top-1 end-1 z-index-2">
    <argon-snackbar
      v-if="snackbar"
      :title="snackbar.title"
      :description="snackbar.message"
      :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
      :color="snackbar.color"
      :close-handler="closeSnackbar"
    />
  </div>
</template>

<style scoped>
.hover:hover {
  cursor: pointer !important;
}

.uri-text {
  display: inline-block;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
